<template>
  <section class="dialer-home">
    <!-- Configuration Error Message -->
    <div v-if="hasMissingProps" class="dialer-error">
      <h2 class="dialer-error__title">❌ Configuração Incompleta</h2>
      <p class="dialer-error__description">
        As seguintes propriedades obrigatórias estão ausentes:
      </p>
      <ul class="dialer-error__list">
        <li 
          v-for="prop in missingProps" 
          :key="prop"
          class="dialer-error__item"
        >
          <strong>{{ prop }}</strong>
        </li>
      </ul>
    </div>

    <div v-else-if="authError" class="dialer-error">
      <h2 class="dialer-error__title">Não foi possível iniciar a chamada</h2>
      <p class="dialer-error__description">{{ authError }}</p>
    </div>

    <div
      v-else-if="isAuthenticating && !isCallView"
      class="dialer-error dialer-error--connecting"
    >
      <p class="dialer-error__description">Conectando à sala...</p>
    </div>

    <!-- Pre-Call Component -->
    <DialerPreCall 
      v-else-if="isPreCallView"
      :primaryUID="devMode ? devUIDs.primaryUID : primaryUID"
      :secondaryUID="devMode ? devUIDs.secondaryUID : secondaryUID"
      :options="options"
      @start-call="handleStartCall"
      @emit-event="handleEmitEvent"
    />
    
    <!-- Call Component -->
    <DialerCall
      v-else-if="isCallView"
      ref="DialerCall"
      :primaryUID="devMode ? devUIDs.primaryUID : primaryUID"
      :secondaryUID="devMode ? devUIDs.secondaryUID : secondaryUID"
      :primaryName="primaryName"
      :secondaryName="secondaryName"
      :system="callSystem"
      :options="options"
      :awaitSecondary="awaitSecondary"
      @left-call="handleLeftCall"
      @emit-event="handleEmitEvent"
    />
  </section>
</template>

<script>
import './DialerHome.css';
import DialerPreCall from './pre-call/DialerPreCall.vue';
import DialerCall from './call/DialerCall.vue';
import { loginCliente, getStatusRoomByUIDs } from '@/services/service-api';
import { version } from '../../../package.json';

/**
 * Estados de visualização do componente Dialer
 * @readonly
 * @enum {string}
 */
const VIEW_STATES = {
  PRE_CALL: 'pre-call',
  CALL: 'call'
};

/**
 * Configuração de props obrigatórias
 * @readonly
 * @type {string[]}
 */
const REQUIRED_PROPS = [
  'systemCode', 'systemPassword', 'primaryUID', 'secondaryUID'
];

/**
 * Valida formato de UID (11 dígitos numéricos)
 * @param {string|number} value - Valor a validar
 * @returns {boolean} true se válido, false caso contrário
 */
function validateUID(value) {
  if (value === null) return true;
  
  const uidStr = String(value);
  
  if (uidStr.length !== 11) {
    console.error('[Dialer] UID deve ter exatamente 11 caracteres, recebido:', uidStr.length);
    return false;
  }
  
  if (!/^[0-9]{11}$/.test(uidStr)) {
    console.error('[Dialer] UID deve conter apenas números (0-9)');
    return false;
  }
  
  return true;
}

export default {
  name: 'DialerHome',
  components: {
    DialerPreCall,
    DialerCall
  },

  props: {
    systemCode: {
      type: String,
      required: false,
      default: null,
      validator: (value) => value === null || value.trim().length > 0
    },
    systemPassword: {
      type: String,
      required: false,
      default: null,
      validator: (value) => value === null || value.trim().length > 0
    },
    primaryUID: {
      type: [String, Number],
      required: false,
      default: null,
      validator: validateUID
    },
    secondaryUID: {
      type: [String, Number],
      required: false,
      default: null,
      validator: validateUID
    },
    client: {
      type: String,
      required: false,
      default: null,
      validator: (value) => {
        if (value === null) return true;
        const client = String(value).trim();
        return client.length > 0 && client.length <= 15;
      }
    },
    options: {
      type: Object,
      default: null,
    },
    skipPreCall: {
      type: Boolean,
      default: false,
    },
    awaitSecondary: {
      type: Boolean,
      default: false,
    },
    primaryName: {
      type: String,
      required: false,
      default: '',
    },
    secondaryName: {
      type: String,
      required: false,
      default: '',
    },
  },
  
  data() {
    return {
      currentView: VIEW_STATES.PRE_CALL,
      preCallData: {},
      missingProps: [],
      isAuthenticating: false,
      authError: '',
      // Configuração de Modo Desenvolvimento
      devMode: true,
      devCredentials: {
        clientCode: 'development', 
        systemPassword: 'abc123'
      },
      devUIDs: {
        primaryUID: '49433966074', 
        secondaryUID: '70676110088'
      }
    };
  },

  computed: {
    isPreCallView() {
      return this.currentView === VIEW_STATES.PRE_CALL;
    },
    isCallView() {
      return this.currentView === VIEW_STATES.CALL;
    },
    hasMissingProps() {
      return this.missingProps.length > 0;
    },
    callSystem() {
      if (this.devMode) {
        return this.devCredentials.clientCode;
      }

      const client = this.client && String(this.client).trim();
      return client || this.systemCode;
    }
  },

  created() {
    if (this.skipPreCall || (this.systemCode && this.primaryUID && this.secondaryUID)) {
      this.devMode = false;
    }

  },

  async beforeMount() {
    this.logComponentInfo();
    await this.initializeComponent();
  },

  mounted() {
  },

  methods: {
    /**
     * Inicializa o componente validando props e autenticando
     * @private
     */
    async initializeComponent() {
      this.validateRequiredProps();

      if (this.hasMissingProps) {
        return;
      }

      this.isAuthenticating = true;
      this.authError = '';

      try {
        const systemData = await this.authenticateSystem();
        if (!systemData) {
          this.authError = 'Não foi possível autenticar no discador. Confirme se a API está no ar.';
          return;
        }

        if (this.skipPreCall) {
          this.currentView = VIEW_STATES.CALL;
        }
      } finally {
        this.isAuthenticating = false;
      }
    },

    /**
     * Registra informações de montagem do componente
     * @private
     */
    logComponentInfo() {
      console.info(`[Dialer] v${version}`);
    },

    /**
     * Valida se todas as props obrigatórias foram fornecidas
     * Em modo desenvolvimento, usa credenciais e UIDs hardcoded
     * Popula o array missingProps com as props ausentes
     * @private
     */
    validateRequiredProps() {
      if (this.devMode) {
        console.warn('[Dialer] ⚠️ MODO DESENVOLVIMENTO ATIVADO - Usando credenciais e UIDs hardcoded');
        console.warn('[Dialer] primaryUID (dev):', this.devUIDs.primaryUID);
        console.warn('[Dialer] secondaryUID (dev):', this.devUIDs.secondaryUID);
        this.missingProps = [];
        return;
      }

      this.missingProps = REQUIRED_PROPS.filter(propName => {
        const value = this[propName];

        if (propName === 'primaryUID' || propName === 'secondaryUID') {
          if (value === undefined || value === null || value === '') {
            return true;
          }

          const uidStr = String(value).replace(/\D/g, '');
          if (!/^[0-9]{11}$/.test(uidStr)) {
            return true;
          }

          return false;
        }

        return !value || value.trim() === '';
      });

      if (this.hasMissingProps) {
        console.error('[Dialer] Props obrigatórias ausentes ou inválidas:', this.missingProps);
      } else {
        console.info('[Dialer] Todas as props obrigatórias presentes e válidas');
      }
    },

    /**
     * Autentica o sistema no backend
     * Em modo dev, usa credenciais hardcoded; caso contrário, das props
     * Emite evento authSystem com resultado da autenticação
     * @private
     * @returns {Promise<Object|void>} Dados de autenticação do sistema
     */
    async authenticateSystem() {
      try {
        console.info('[Dialer] Autenticando sistema...');
        
        const credentials = this.devMode ? this.devCredentials : {
          clientCode: this.systemCode,
          systemPassword: this.systemPassword
        };
        
        const systemData = await loginCliente({
          codigo: credentials.clientCode,
          senha: credentials.systemPassword
        });

        this.emitCustomEvent('authSystem', { 
          status: true, 
          ...systemData 
        });

        console.info('[Dialer] Sistema autenticado');
        return systemData;
      } catch (error) {
        console.error('[Dialer] Falha na autenticação:', error);
        this.emitCustomEvent('authSystem', { 
          status: false, 
          error: error.message 
        });
      }
    },

    /**
     * Emissor centralizado de eventos
     * @private
     * @param {string} eventName - Nome do evento a emitir
     * @param {*} [data=null] - Dados do payload do evento
     */
    emitCustomEvent(eventName, data = null) {
      this.$emit(eventName, data);
    },

    /**
     * Manipula emissão de eventos de componentes filhos
     * @public
     * @param {Object} payload - Payload do evento
     * @param {string} payload.eventName - Nome do evento
     * @param {*} payload.data - Dados do evento
     */
    handleEmitEvent({ eventName, data }) {
      this.emitCustomEvent(eventName, data);
    },

    /**
     * Manipula transição da pré-chamada para a chamada
     * @public
     * @param {Object} data - Dados de configuração da pré-chamada
     */
    handleStartCall(data) {
      console.info('[Dialer] Transicionando para visualização de chamada');
      this.preCallData = data;
      this.currentView = VIEW_STATES.CALL;
    },

    /**
     * Manipula usuário saindo da chamada
     * Com skipPreCall, não volta à pré-chamada (o host fecha via leaveRoom).
     * @public
     */
    handleLeftCall() {
      if (this.skipPreCall) {
        return;
      }

      console.info('[Dialer] Retornando para visualização de pré-chamada');
      this.currentView = VIEW_STATES.PRE_CALL;
      this.preCallData = {};
    },

    /**
     * Obtém o estado atual do componente
     * @public
     * @returns {Object} Objeto com estado atual
     */
    getCurrentState() {
      return {
        currentView: this.currentView,
        preCallData: this.preCallData
      };
    },

    /**
     * Aciona autenticação do sistema manualmente
     * @public
     * @returns {Promise<Object>} Resultado da autenticação
     */
    async authSystem() {
      return await this.authenticateSystem();
    },

    /**
     * Obtém os UIDs dos participantes
     * Em modo dev, retorna os UIDs hardcoded; caso contrário, das props
     * @public
     * @returns {Object} Objeto com primaryUID e secondaryUID
     */
    getUIDs() {
      if (this.devMode) {
        return {
          primaryUID: this.devUIDs.primaryUID,
          secondaryUID: this.devUIDs.secondaryUID
        };
      }
      
      return {
        primaryUID: this.primaryUID,
        secondaryUID: this.secondaryUID
      };
    },

    /**
     * Obtém o status da sala pelos UIDs dos participantes
     * Em modo dev, usa UIDs hardcoded; caso contrário, das props
     * @public
     * @returns {Promise<Object>} Dados do status da sala
     */
    async getStatusRoom() {
      try {
        const uids = this.getUIDs();
        const roomStatus = await getStatusRoomByUIDs(
          uids.primaryUID, 
          uids.secondaryUID
        );
        return roomStatus;
      } catch (error) {
        console.error('[Dialer] Falha ao obter status da sala:', error);
        throw error;
      }
    },
    logMessage(message) {
      console.log('@A4 [DialerHome.vue] logMessage', message)
    },
    handleCloseDiscadorIframe() {
      console.log('@A4 [DialerHome.vue] handleCloseDiscadorIframe')
      console.log('@A4 [DialerHome.vue] handleCloseDiscadorIframe.confirmLeaveCall')
      this.$refs?.DialerCall?.confirmLeaveCall()
    }
  }
};
</script>
